import { useRef, type MouseEvent } from 'react';
import { times } from 'lodash';
import { ButtonType } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import {
  ButtonWithDropdown,
  simpleNotifier,
  type DropdownItem,
} from '@ardoq/dropdown-menu';
import type { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { Switch } from '@ardoq/forms';
import { Icon, IconName, IconSize, InfoIcon } from '@ardoq/icons';
import {
  PlainTextPopover,
  TextOverflow,
  WithUncontrolledPopover,
} from '@ardoq/popovers';
import { Tag } from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import {
  Container,
  ControlSlot,
  Controls,
  InfoSlot,
  NestingLine,
  Row,
} from './atoms';

export type SettingStringLevel = 1 | 2 | 3;

type SettingStringCommonProps = {
  title: string;
  level?: SettingStringLevel;
  iconName?: IconName;
  info?: string;
  isDisabled?: boolean;
};

export type SettingStringOptionProps = SettingStringCommonProps & {
  type: 'option';
  value: string | null;
  options: DropdownItem[];
  isKeepOpen?: boolean;
  dropdownSize?: DropdownSize;
  maxDropdownSize?: DropdownSize;
  maxDropdownHeight?: number;
};

export type SettingStringToggleProps = SettingStringCommonProps & {
  type: 'toggle';
  name: string;
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
};

export type SettingStringProps =
  | SettingStringOptionProps
  | SettingStringToggleProps;

export const SettingString = (props: SettingStringProps) => {
  const { title, level = 1, iconName, info, isDisabled = false } = props;
  const notifier = useRef(simpleNotifier());
  const controlRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const iconColor = isDisabled ? colors.iconActionDisabled : colors.icon;

  const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as Node;
    if (
      isDisabled ||
      controlRef.current?.contains(target) ||
      infoRef.current?.contains(target)
    ) {
      return;
    }
    if (props.type === 'option') {
      notifier.current.notify();
      return;
    }
    props.onChange(!props.isChecked);
  };

  return (
    <Container>
      {times(level - 1, depth => (
        <NestingLine key={depth} $depth={depth} />
      ))}
      <Row
        role="option"
        aria-label={title}
        aria-disabled={isDisabled || undefined}
        $level={level}
        $isDisabled={isDisabled}
        onClick={onRowClick}
      >
        {iconName && (
          <Icon
            iconName={iconName}
            iconSize={IconSize.LARGE}
            color={iconColor}
          />
        )}
        <Text
          variant="text1"
          color={isDisabled ? 'textDisabled' : 'textDefault'}
        >
          {title}
        </Text>
        {info && (
          <InfoSlot ref={infoRef}>
            <WithUncontrolledPopover
              content={<PlainTextPopover>{info}</PlainTextPopover>}
            >
              <InfoIcon iconSize={IconSize.MEDIUM} color={iconColor} />
            </WithUncontrolledPopover>
          </InfoSlot>
        )}
        <Controls>
          {props.type === 'option' && props.value && (
            <Tag isDisabled={isDisabled}>
              <TextOverflow lineClamp={2}>{props.value}</TextOverflow>
            </Tag>
          )}
          <ControlSlot ref={controlRef}>
            {props.type === 'option' ? (
              <ButtonWithDropdown
                iconName={IconName.CHEVRON_RIGHT}
                aria-label={title}
                options={props.options}
                isKeepOpen={props.isKeepOpen}
                dropdownSize={props.dropdownSize}
                maxDropdownSize={props.maxDropdownSize}
                maxDropdownHeight={props.maxDropdownHeight}
                requestOpen={notifier.current}
                buttonType={ButtonType.GHOST}
                isDisabled={isDisabled}
              />
            ) : (
              <Switch
                name={props.name}
                aria-label={title}
                isChecked={props.isChecked}
                onChange={props.onChange}
                isDisabled={isDisabled}
              />
            )}
          </ControlSlot>
        </Controls>
      </Row>
    </Container>
  );
};
